import { type TranslatableFigureInfoKey } from '../figureInfo'

// 日本語/英語からキーへのマッピング（データマイグレーション期間中の互換性のため）
const VALUE_TO_KEY_MAP: Record<string, string> = {
  // dataDisplayType
  '%': 'percentage',
  サンプル数: 'sample_size',
  'Sample Size': 'sample_size',
  推定人口: 'estimated_population',
  'Estimated Population': 'estimated_population',
  // segmentDisplayType
  '5segs': 'five_segs',
  '9segs': 'nine_segs',
  同時表示: 'both',
  Both: 'both',
  まとめ: 'summary',
  Summary: 'summary',
  // resultViewerType
  '個別設問+グラフ表示': 'show_tables_graphs',
  'Show Tables + Graphs': 'show_tables_graphs',
  設問連続表示: 'show_table_only',
  'Show Table only': 'show_table_only',
  // tableChartType
  グラフ: 'graph',
  Graph: 'graph',
  表: 'table',
  Table: 'table',
}

// figureType 用のマッピング
const FIGURE_TYPE_VALUE_TO_KEY_MAP: Record<string, string> = {
  // カスタマイズ分析
  個別設問: 'individual_questionnaire',
  'Individual Question': 'individual_questionnaire',
  グループ別マトリクス: 'group_matrix',
  'Group Matrix': 'group_matrix',
  連続設問: 'continuous_questionnaire',
  'Continuous Question': 'continuous_questionnaire',
  // 時系列分析
  '9segs顧客分布': '9segs_customer_distribution',
  '9segs Distribution': '9segs_customer_distribution',
  認知: 'aware',
  Aware: 'aware',
  既存顧客: 'existing_customer',
  'Existing Customers': 'existing_customer',
  ロイヤル顧客: 'loyal_customer',
  'Loyal Customer': 'loyal_customer',
  'ユーザー転換率\n（認知者内）': 'user_conversion_rateamong_aware',
  'Conversion Rate\n(among Aware Customers)': 'user_conversion_rateamong_aware',
  'ロイヤル率\n（既存顧客内）':
    'loyal_customersty_rateamong_existing_customers',
  'Loyal Customer Rate': 'loyal_customersty_rateamong_existing_customers',
  NPI: 'npi',
  'a-NPI': 'aNpi',
  'n-NPI': 'nNpi',
  'u-NPI': 'uNpi',
  // tableChartType（時系列分析の複合キーで使用）
  グラフ: 'graph',
  Graph: 'graph',
  表: 'table',
  Table: 'table',
}

// screenLabel 用のマッピング
const SCREEN_LABEL_VALUE_TO_KEY_MAP: Record<string, string> = {
  // pathGroupLabel
  詳細: 'deep_dive_analysis',
  'Deep Dive Analysis': 'deep_dive_analysis',
  基礎: 'core_analysis',
  'Core Analysis': 'core_analysis',
  時系列: 'time_series',
  'Time Series': 'time_series',
  顧客戦略: 'customer_strategy',
  'Customer Strategy': 'customer_strategy',
  ブランドアカウント設定: 'brand_account_settings',
  'Brand Account Settings': 'brand_account_settings',
  ドキュメント: 'documents_1',
  Documents: 'documents_1',
  サーベイ設定: 'survey_settings',
  'Survey Settings': 'survey_settings',
  // pathLabel
  カスタマイズ分析: '9segs_flexible_mode',
  '9segs Custom Comparison': '9segs_flexible_mode',
  '9segs顧客データ': '9segs_customer_data',
  '9segs Customer Data': '9segs_customer_data',
  '9segs顧客分布': '9segs_customer_distribution',
  '9segs Distribution': '9segs_customer_distribution',
  オーバーラップ分析: 'overlap_analysis',
  'Overlap Analysis': 'overlap_analysis',
  顧客属性分析: 'customized_segments_analysis',
  'Custom Segment Analysis': 'customized_segments_analysis',
  時系列分析: 'time_series_analysis',
  'Time Series Analysis': 'time_series_analysis',
}

/**
 * DBから取得した値（日本語/英語/キー）をキーに正規化する
 * データマイグレーション完了後は不要になる
 *
 * @param figureInfoKey - 翻訳対象のキー
 * @param value - 正規化する値
 * @returns 正規化されたキー、マップにない場合は元の値
 */
export function normalizeValueToKey(
  figureInfoKey: TranslatableFigureInfoKey,
  value: string,
): string {
  switch (figureInfoKey) {
    case 'dataDisplayType':
    case 'segmentDisplayType':
    case 'resultViewerType':
    case 'tableChartType':
      // マップに存在 → キーを返す、存在しない → そのまま返す
      return VALUE_TO_KEY_MAP[value] ?? value
    case 'figureType':
      // " - " で区切られた複合キー（例: 既存顧客 - グラフ）を個別に正規化
      return value
        .split(' - ')
        .map((part) => FIGURE_TYPE_VALUE_TO_KEY_MAP[part.trim()] ?? part.trim())
        .join(' - ')
    case 'screenLabel':
      // " - " でスプリットして各部分を個別に正規化
      return value
        .split(' - ')
        .map(
          (part) => SCREEN_LABEL_VALUE_TO_KEY_MAP[part.trim()] ?? part.trim(),
        )
        .join(' - ')
    default:
      return value
  }
}
